import { MessageList } from './MessageList'
import { MessageInput } from './MessageInput'
import { QuickReplies } from './QuickReplies'
import { EscalationBanner } from './EscalationBanner'
import { useChat } from '@/contexts/ChatContext'
import { useAuth } from '@/contexts/AuthContext'
import { useOperator } from '@/contexts/OperatorContext'
import { motion, AnimatePresence } from 'framer-motion'
import {
  BarChart3, TrendingUp, Users, Zap, Calendar,
  FileUp, Wrench,
  type LucideIcon,
} from 'lucide-react'
import type { TenantFeatures } from '@/contexts/OperatorContext'

const EVENT_PROMPTS: { label: string; icon: LucideIcon }[] = [
  { label: 'How is this event doing compared to last year?', icon: TrendingUp },
  { label: 'What are the top selling plans at this event?', icon: BarChart3 },
  { label: 'Show me today\'s hourly revenue breakdown', icon: Zap },
  { label: 'How many guests have we had so far?', icon: Users },
]

const NO_EVENT_PROMPTS: { label: string; icon: LucideIcon }[] = [
  { label: 'Which event had the most revenue this year?', icon: TrendingUp },
  { label: 'Show me a summary of my recent events', icon: BarChart3 },
  { label: 'What can you help me with?', icon: Zap },
]

function buildCapabilities(features: TenantFeatures) {
  const caps: { title: string; icon: LucideIcon; items: string[] }[] = [
    {
      title: 'Analytics',
      icon: BarChart3,
      items: ['Revenue by event, day, or plan', 'Year-over-year comparisons', 'Peak hours & attendance'],
    },
  ]

  if (features.write_tools) {
    caps.push({
      title: 'Event Setup',
      icon: Calendar,
      items: ['Create & clone events', 'Manage plans & pricing', 'Schedule sale windows'],
    })
  }

  caps.push({
    title: 'Operations',
    icon: Wrench,
    items: ['Scanner troubleshooting', 'Kiosk group management', 'Ride inspection tools'],
  })

  caps.push({
    title: 'Reports',
    icon: FileUp,
    items: features.smart_import
      ? ['Settlement previews', 'Smart barcode import', 'Custom report canvases']
      : ['Settlement previews', 'Custom report canvases'],
  })

  return caps
}

export function ChatInterface() {
  const { isEscalated, handoffPending, messages, quickReplies, sendMessage } = useChat()
  const { currentEventId } = useAuth()
  const { features } = useOperator()
  const prompts = currentEventId != null ? EVENT_PROMPTS : NO_EVENT_PROMPTS
  const capabilities = buildCapabilities(features)

  return (
    <div data-testid="chat-interface-root" className="relative flex flex-col flex-1 min-h-0 overflow-hidden">
      {/* Escalation Banner */}
      <AnimatePresence>
        {(isEscalated || handoffPending) && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
          >
            <EscalationBanner />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col min-h-0 relative">
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <MessageList />
        </div>

        {/* Starter Prompts — event-scoped when an event is selected */}
        <AnimatePresence>
          {messages.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="px-4 pb-2"
            >
              <p className="text-xs font-medium text-ink-soft dark:text-slate-400 mb-2">
                {currentEventId != null ? 'Ask about this event:' : 'Try asking:'}
              </p>
              <div className="flex flex-wrap gap-2">
                {prompts.map((prompt, i) => {
                  const Icon = prompt.icon
                  return (
                    <motion.button
                      key={prompt.label}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.05 }}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => sendMessage(prompt.label)}
                      className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-card
                        bg-white dark:bg-slate-800 border border-hairline dark:border-slate-600
                        text-ink-dark dark:text-slate-200
                        hover:bg-butler-50 dark:hover:bg-butler-900/20
                        hover:border-butler-300 dark:hover:border-butler-500
                        shadow-card-soft transition-colors"
                    >
                      <Icon className="h-3.5 w-3.5 text-butler-600 dark:text-butler-300" aria-hidden="true" />
                      <span>{prompt.label}</span>
                    </motion.button>
                  )
                })}
              </div>

              {/* Capability grid — adapts per tenant feature flags */}
              <div data-testid="capability-grid" className={`mt-4 grid grid-cols-2 gap-2 ${capabilities.length <= 3 ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
                {capabilities.map((cap) => {
                  const CIcon = cap.icon
                  return (
                    <div
                      key={cap.title}
                      className="rounded-card border border-hairline bg-white/60 p-3 dark:border-slate-700 dark:bg-slate-800/60"
                    >
                      <div className="flex items-center gap-1.5 mb-1.5">
                        <CIcon className="h-3.5 w-3.5 text-butler-600 dark:text-butler-300" aria-hidden="true" />
                        <span className="text-[11px] font-semibold uppercase tracking-wide text-butler-700 dark:text-butler-200">
                          {cap.title}
                        </span>
                      </div>
                      <ul className="space-y-0.5">
                        {cap.items.map((item) => (
                          <li key={item} className="text-[11px] leading-snug text-ink-soft dark:text-slate-400">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Replies */}
        <AnimatePresence>
          {quickReplies.length > 0 && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="overflow-hidden border-t border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm"
            >
              <QuickReplies />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Message Input */}
        <div className="border-t border-slate-200 dark:border-slate-700 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm">
          <MessageInput />
        </div>
      </div>

      {/*
        Historical note — Fork U1 (2026-04-18) removed the dueling
        "Butler beta ops console" absolute-inset welcome. It collided with
        MessageList's own empty state on every route of the Owner Hub. The
        single SMS-friendly greeting now lives in MessageList.
      */}
    </div>
  )
}
